
import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

interface AddressMapProps {
  address: string;
  className?: string;
}

const AddressMap = ({ address, className = "" }: AddressMapProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Use the provided Mapbox access token
    mapboxgl.accessToken = "pk.eyJ1IjoieWFuaXN0YWllYjEiLCJhIjoiY205Z3dhMjZyMXh3YjJrczU2cjR2MmhneCJ9.ZVaShyTgfR8J_s2RTAwj6w";

    if (!mapContainer.current) return;
    
    setLoading(true);
    setError(null);

    try {
      // The coordinates for Rue des Jeuneurs, 75002 Paris, France
      const defaultCoordinates: [number, number] = [2.345, 48.868]; // [lng, lat]

      // Initialize the map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/streets-v12",
        center: defaultCoordinates,
        zoom: 15,
      });

      // Add navigation controls
      map.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "top-right"
      );

      // Add marker for the address
      marker.current = new mapboxgl.Marker({ color: "#FF6B6B" })
        .setLngLat(defaultCoordinates)
        .setPopup(new mapboxgl.Popup().setHTML(`<strong>Legatia SA</strong><br>${address}`))
        .addTo(map.current);

      // Wait for map to load
      map.current.on("load", () => {
        setLoading(false);
      });

      // Handle errors
      map.current.on("error", (e) => {
        console.error("Mapbox error:", e);
        setError("Error loading map. Please try again later.");
        setLoading(false);
      });
    } catch (err) {
      console.error("Map initialization error:", err);
      setError("Error initializing map. Please try again later.");
      setLoading(false);
    }

    return () => {
      // Clean up map and marker when component unmounts
      if (marker.current) {
        marker.current.remove();
      }
      if (map.current) {
        map.current.remove();
      }
    };
  }, [address]); // Re-initialize map when address changes

  return (
    <div className={`rounded-lg overflow-hidden shadow-md border border-gray-200 ${className}`}>
      <div ref={mapContainer} style={{ height: "250px", width: "100%" }}>
        {loading && (
          <div className="flex items-center justify-center h-full bg-gray-100">
            <div className="animate-spin h-8 w-8 border-4 border-legatia-blue border-t-transparent rounded-full"></div>
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full bg-gray-100 text-red-500 p-4 text-center">
            <div>{error}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddressMap;
